import React, {FC, useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';

import {fetchUser} from '~src/modules/users/actions/user.actions';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import TileHeader from '~src/shared/headers/tileHeader/TileHeader';
import {UserFormValues} from '~src/modules/users/interfaces';
import {getUserFullName, User} from '~src/interfaces/user.interface';
import {ApiReduxResp} from '~src/services/apiMiddleware';
import {StoreI} from '~src/rootReducer';
import CollapsibleListItem from '~src/shared/lists/collapsibleListItem/CollapsibleListItem';

import UserForm from './UserForm';
import {FormContainer, ListItem, SpinnerContainer} from './userListItem.sc';
import {AvatarType} from "~src/shared/avatarBox/AvatarBox";

interface OwnProps {
  onSubmit: (userId: string, values: UserFormValues, initialValues: Partial<UserFormValues>) => Promise<any>;
  user: User;
  isOpen: boolean;
  changeOpenItem: (userId?: string) => void;
  onDelete: (user: User) => Promise<void>;
  onDeactivationToggle: (user: User) => Promise<void>;
}

interface DispatchProps {
  fetchUser: (userId: string) => ApiReduxResp<User>;
}

interface StateProps {
  userDetails: User | null;
}

type Props = OwnProps & DispatchProps & StateProps;

const UserListItem: FC<Props> = props => {
  const [fullName, setFullName] = useState(getUserFullName(props.user));
  const [openLoading, setOpenLoading] = useState(false);

  useEffect(() => {
    setFullName(getUserFullName(props.user));
  }, [props.user.firstname, props.user.lastname]);

  const onSubmit = (values: UserFormValues, initialValues: Partial<UserFormValues>) => {
    return props.onSubmit(props.user.uuid, values, initialValues).then(res => {
      if (res && res.errors) {
        return res.errors;
      } else {
        toggleForm();
      }
    });
  };

  function toggleForm() {
    if (!props.isOpen) {
      setOpenLoading(true);
      props.changeOpenItem(props.user.uuid);
      props.fetchUser(props.user.uuid).then(() => {
        setOpenLoading(false);
      });
    } else {
      props.changeOpenItem();
    }
  }

  const header = useMemo(
    () => (
      <TileHeader
        title={fullName}
        tooltip={'<table><tr><th>uuid</th><td>' + props.user.uuid + '</td></tr><tr><th>created_at</th><td>' + props.user.created_at + '</td></tr></table>'}
        avatar={{ type: AvatarType.User, url: props.user.imageUrl, name: fullName }}
      />
    ),
    [fullName, props.user.imageUrl],
  );

  useEffect(() => {
    if (props.isOpen) {
      // scroll to the opened user
      const el = document.getElementById(`user-${props.user.uuid}`);
      if (el && el.getBoundingClientRect().bottom > window.innerHeight) {
        el.scrollIntoView();
      }
    }
  }, [props.isOpen]);

  return (
    <ListItem id={`user-${props.user.uuid}`}>
      <CollapsibleListItem header={header} onHeaderClick={toggleForm} maxHeight={950} isOpen={props.isOpen}>
        <FormContainer>
          {openLoading ? (
            <SpinnerContainer>
              <LoadingSpinner />
            </SpinnerContainer>
          ) : (
            <UserForm
              user={props.userDetails}
              onCancel={toggleForm}
              onFrmSubmit={onSubmit}
              onDelete={props.onDelete}
              onDeactivate={props.onDeactivationToggle}
            />
          )}
        </FormContainer>
      </CollapsibleListItem>
    </ListItem>
  );
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchUser: uuid => dispatch(fetchUser(uuid)),
});

const mapStateToProps = (state: StoreI): StateProps => ({
  userDetails: state.userManagement.userDetails,
});

const UserListItemSuperComponent = connect(mapStateToProps, mapDispatchToProps)(UserListItem);

export default React.memo(UserListItemSuperComponent);
